import { useState } from "react";
import { FileUp, Loader2 } from "lucide-react";
import { MetaRatesImportResult } from "@/models/pricing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useImportMetaRates } from "../hooks/useImportMetaRates";

const META_PRICING_URL = "https://developers.facebook.com/docs/whatsapp/pricing";

/** The file's bytes as base64, in chunks so a large file can't overflow the stack. */
const toBase64 = async (file: File): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return window.btoa(binary);
};

interface ImportRatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportRatesDialog({ open, onOpenChange }: ImportRatesDialogProps) {
  const { mutate: importRates, isPending } = useImportMetaRates();
  const [file, setFile] = useState<File | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState<MetaRatesImportResult | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFile(null);
      setResult(null);
    }
    onOpenChange(next);
  };

  const handleImport = async () => {
    if (!file) return;
    importRates(
      {
        file: await toBase64(file),
        effectiveFrom: new Date(`${effectiveFrom}T00:00:00.000Z`).toISOString()
      },
      { onSuccess: setResult }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Meta’s rate card</DialogTitle>
          <DialogDescription>
            Meta has no API for its prices. Download the “USD list rates” file from{" "}
            <a
              href={META_PRICING_URL}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              Meta’s pricing page
            </a>{" "}
            and upload it as it is. Regions such as “Rest of Middle East” apply to each of their
            countries, and “Other” becomes the default rate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rate-card-file">Rate card file</Label>
            <Input
              id="rate-card-file"
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setResult(null);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate-card-date">Prices start on (the card’s effective date)</Label>
            <Input
              id="rate-card-date"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </div>

          {result && (
            <div className="rounded-md border p-3 text-sm">
              <p className="font-medium">
                {result.imported} countries priced, {result.fromRegions} of them from a regional
                rate.
              </p>
              {result.skipped.length > 0 && (
                <>
                  <p className="mt-2 text-muted-foreground">Rows not imported:</p>
                  <ul className="mt-1 max-h-40 list-disc overflow-y-auto pl-5 text-muted-foreground">
                    {result.skipped.map((s) => (
                      <li key={`${s.row}-${s.value}`}>
                        Row {s.row}: {s.value || "(empty)"} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={handleImport} disabled={!file || !effectiveFrom || isPending}>
              {isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileUp className="mr-2 h-4 w-4" />
              )}
              Import
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
